import { Link } from 'react-router-dom';

const HERO_IMAGE = 'https://public.readdy.ai/ai/img_res/95c2410ceeea1ce46556262db2b9254e.jpg';
const PHONE = '(708) 698-4072';
const PHONE_HREF = 'tel:7086984072';

export default function HeroSection() {
  return (
    <section
      className="relative min-h-screen flex items-center justify-start overflow-hidden"
      style={{
        backgroundImage: `url('${HERO_IMAGE}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 30%',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Multi-layer depth overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/20" />
      {/* Subtle vignette */}
      <div
        className="absolute inset-0 opacity-40"
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.8) 100%)' }}
      />
      {/* Brand green subtle tint on left */}
      <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-brand-green/15 to-transparent" />

      {/* Decorative diagonal accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-brand-gold/60 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-28">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="animate-hero-badge inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/50 rounded-full px-4 py-1.5 mb-8 backdrop-blur-sm">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-shield-star-fill text-brand-gold text-xs animate-pulse-soft" />
            </div>
            <span className="text-brand-gold text-xs font-bold uppercase tracking-widest">
              Chicago&apos;s Most Trusted Since 1989
            </span>
          </div>

          {/* Headline */}
          <h1 className="animate-hero-h1 font-display text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.08] mb-6 text-shadow-lg">
            Chicago&apos;s Premier<br />
            <span className="text-brand-gold italic">Roofing &amp;</span><br />
            <span className="text-white">Construction Experts</span>
          </h1>

          {/* Subheadline */}
          <p className="animate-hero-sub text-base lg:text-lg text-white/80 leading-relaxed mb-10 max-w-xl">
            Family-owned and operated since 1989. Residential &amp; commercial roofing, tuckpointing, masonry,
            siding, and exterior construction — all backed by a free written estimate and 35+ years of
            Chicagoland expertise.
          </p>

          {/* CTA buttons */}
          <div className="animate-hero-cta flex flex-col sm:flex-row gap-4 mb-12">
            <Link
              to="/contact"
              className="group relative bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-8 py-4 rounded-md transition-all text-center cursor-pointer whitespace-nowrap overflow-hidden"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                <i className="ri-calendar-check-line text-base" />
                Get Your Free Roof Inspection
              </span>
              <div className="absolute inset-0 gradient-shimmer" />
            </Link>
            <a
              href={PHONE_HREF}
              className="group border-2 border-white/50 hover:border-brand-gold hover:bg-white/10 text-white text-sm font-bold px-8 py-4 rounded-md transition-all text-center flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              <div className="w-5 h-5 flex items-center justify-center bg-brand-gold/20 rounded-full">
                <i className="ri-phone-fill text-brand-gold text-xs" />
              </div>
              Call {PHONE}
            </a>
          </div>

          {/* Trust micro-badges */}
          <div className="animate-hero-badges flex flex-wrap gap-5">
            {[
              { icon: 'ri-award-fill', label: '35+ Years Experience' },
              { icon: 'ri-shield-check-fill', label: 'Licensed & Insured' },
              { icon: 'ri-home-heart-fill', label: 'Family-Owned' },
              { icon: 'ri-file-text-fill', label: 'Free Written Estimates' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 bg-white/8 backdrop-blur-sm rounded-full px-3 py-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-brand-gold text-xs`} />
                </div>
                <span className="text-white/85 text-xs font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating project count badge */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3 animate-float">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 text-center">
          <div className="font-display text-3xl font-bold text-brand-gold">5,000+</div>
          <div className="text-white/70 text-xs mt-1">Projects<br />Completed</div>
        </div>
        <div className="bg-brand-gold/90 backdrop-blur-md rounded-2xl p-5 text-center" style={{ animationDelay: '0.5s' }}>
          <div className="font-display text-3xl font-bold text-white">35+</div>
          <div className="text-white/80 text-xs mt-1">Years<br />in Business</div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-scroll-bounce">
        <span className="text-white/30 text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-5 h-8 border-2 border-white/25 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-white/50 rounded-full animate-scroll-bounce" />
        </div>
      </div>

      {/* Bottom overlap element - pulls next section up visually */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-brand-dark to-transparent" />
    </section>
  );
}
